import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    ReactNode,
} from 'react';
import { useGetV1AbsenceReasons } from '../../api/generated/endpoint';
import {
    AbsenceReasonsResponse,
    ReasonInformation,
} from 'api/generated/models';
import { DeclarationTypes } from 'pages/SubmissionFormWizard/SubmissionFormWizard';
import { ErrorType } from 'api/mutator/custom-instance';

type AbsenceReasonsContextProps = {
    absenceReasons: AbsenceReasonsResponse[] | undefined;
    absenceReasonsIsPending: boolean;
    absenseReasonsError: ErrorType<null> | null;
    plannedAbsenceReasons: ReasonInformation[] | undefined;
    unforeseenAbsenceReasons: ReasonInformation[] | undefined;
};

const AbsenceReasonsContext = createContext<
    AbsenceReasonsContextProps | undefined
>(undefined);

const AbsenceReasonsProvider: React.FC<{
    children: ReactNode;
    userInfo: any;
}> = ({ children, userInfo }) => {
    const {
        data: absenceReasons,
        isPending: absenceReasonsIsPending,
        error: absenseReasonsError,
    } = useGetV1AbsenceReasons({
        query: {
            enabled: !!userInfo,
        },
    });

    const [plannedAbsenceReasons, setPlannedAbsenceReasons] = useState<
        ReasonInformation[] | undefined
    >(undefined);

    const getPlannedAbsenceReasons = useCallback(() => {
        if (Array.isArray(absenceReasons)) {
            const plannedAbsenceResponse = absenceReasons.filter(
                (absenceReason: AbsenceReasonsResponse) => {
                    return (
                        absenceReason.declarationType?.toLowerCase() ===
                        DeclarationTypes.Planned.toLowerCase()
                    );
                }
            );
            return plannedAbsenceResponse[0]?.absenceInformation || [];
        }
        return [];
    }, [absenceReasons]);

    useEffect(() => {
        const reasons = getPlannedAbsenceReasons();
        setPlannedAbsenceReasons(reasons);
    }, [getPlannedAbsenceReasons]);

    const [unforeseenAbsenceReasons, setUnforeseenAbsenceReasons] = useState<
        ReasonInformation[] | undefined
    >(undefined);

    const getUnforeseenAbsenceReasons = useCallback(() => {
        if (Array.isArray(absenceReasons)) {
            const unforeseenAbsenceResponse = absenceReasons.filter(
                (absenceReason: AbsenceReasonsResponse) => {
                    return (
                        absenceReason.declarationType?.toLowerCase() ===
                        DeclarationTypes.Unforeseen.toLowerCase()
                    );
                }
            );
            return unforeseenAbsenceResponse[0]?.absenceInformation || [];
        }
        return [];
    }, [absenceReasons]);

    useEffect(() => {
        const reasons = getUnforeseenAbsenceReasons();
        setUnforeseenAbsenceReasons(reasons);
    }, [getUnforeseenAbsenceReasons]);

    return (
        <AbsenceReasonsContext.Provider
            value={{
                absenceReasons,
                absenceReasonsIsPending,
                absenseReasonsError,
                plannedAbsenceReasons,
                unforeseenAbsenceReasons,
            }}
        >
            {children}
        </AbsenceReasonsContext.Provider>
    );
};

export default AbsenceReasonsProvider;

export const useAbsenceReasons = () => {
    const context = useContext(AbsenceReasonsContext);
    if (context === undefined) {
        throw new Error(
            'useAbsenceReasons must be used within an AbsenceReasonsProvider'
        );
    }
    return context;
};
